import { apiClient } from "@/api";

export const ApplicationApi = {
  /**
   * Получение списка заявок по статусу
   * @param {String} status - Статус заявок (new, in_process, in_transit, completed, canceled)
   * @param {Object} params - Дополнительные параметры запроса
   * @returns {Promise}
   */
  async fetchApplicationsByStatus(status, params = {}) {
    return apiClient.get("/applications", {
      params: {
        ...params,
        status,
      },
    });
  },

  /**
   * Получение всех заявок без фильтрации по статусу
   * @param {Object} params - Параметры запроса (пагинация и т.д.)
   * @returns {Promise<Object>}
   */
  async fetchAllApplications(params = {}) {
    return apiClient.get("/applications", { params });
  },

  /**
   * Получение количества заявок по каждому статусу
   * @param {Object} params - Фильтры (counterparty_id, transport_id, tank_id, date_from, date_to)
   * @returns {Promise<Object>} { new, in_process, in_transit, completed, canceled, total }
   */
  async fetchStatusCounts(params = {}) {
    return apiClient.get("/applications/status-counts", { params });
  },

  /**
   * Получение заявки по ID
   * @param {Number|String} id - ID заявки
   * @returns {Promise<Object>}
   */
  async fetchApplicationById(id) {
    return apiClient.get(`/applications/${id}`);
  },

  /**
   * Создание новой заявки
   * @param {Object} data - Данные новой заявки
   * @returns {Promise<Object>}
   */
  async createApplication(data) {
    if (data.applications && data.applications.length) {
      return apiClient.post("/applications/batch-dispatch", data);
    } else {
      return apiClient.post("/applications", data);
    }
  },

  /**
   * Обновление существующей заявки
   * @param {Number|String} id - ID заявки
   * @param {Object} data - Данные для обновления заявки
   * @returns {Promise<Object>}
   */
  async updateApplication(id, data) {
    return apiClient.put(`/applications/${id}`, data);
  },

  /**
   * Получение истории заявки по ID
   * @param {Number|String} id - ID заявки
   * @param {Object} params - Параметры запроса (пагинация и т.д.)
   * @returns {Promise<Object>}
   */
  async fetchApplicationHistory(id, params = {}) {
    return apiClient.get(`/applications/${id}/history`, { params });
  },

  /**
   * Удаление заявки
   * @param {Number|String} id - ID заявки
   * @param {Object} data - Данные для удаления (причина отмены, комментарий)
   * @returns {Promise<Object>}
   */
  async deleteApplication(id, data) {
    return apiClient.delete(`/applications/${id}`, { data });
  },

  /**
   * Получение списка причин задержки/отмены
   * @returns {Promise}
   */
  async fetchDelayReasons() {
    return apiClient.get("/delay-reasons");
  },

  /**
   * Изменение статуса заявки
   * @param {Number|String} id - ID заявки
   * @param {String} status - Новый статус (new, in_process, in_transit, completed, canceled)
   * @returns {Promise<Object>}
   */
  async changeApplicationStatus(id, status) {
    return apiClient.post(`/applications/${id}/change-status`, { status });
  },

  /**
   * Ввод фактической загрузки по отсекам
   * @param {Number|String} id - ID заявки
   * @param {Object} data - { lines: [{ application_fuel_id, compartments: [{ compartment_id, actual_volume }] }] }
   * @returns {Promise<Object>} { data: ApplicationResource, warnings: [] }
   */
  async updateActualLoading(id, data) {
    return apiClient.put(`/applications/${id}/actual-loading`, data);
  },

  /**
   * Общий список возвратов остатков (страница диспетчера)
   * @param {Object} params - { status, transport_id, date_from, date_to, page, per_page }
   * @returns {Promise<Object>} { data: ApplicationReturnResource[], meta }
   */
  async getReturns(params = {}) {
    return apiClient.get("/applications/returns", { params });
  },

  /**
   * Карточка возврата
   * @param {Number|String} returnId - ID возврата
   * @returns {Promise<Object>}
   */
  async getReturn(returnId) {
    return apiClient.get(`/applications/returns/${returnId}`);
  },

  /**
   * Возвраты конкретной заявки
   * @param {Number|String} id - ID заявки
   * @returns {Promise<Object>}
   */
  async getApplicationReturns(id) {
    return apiClient.get(`/applications/${id}/returns`);
  },

  /**
   * Распределение остатка: на базу (type=to_base) или на другую заявку (type=to_application)
   * @param {Object} data - { type, destination_tank_id?, destination_application_id?, destination_fuel_id?, comment?, items: [{ return_item_id, volume }] }
   * @returns {Promise<Object>}
   */
  async allocateReturn(data) {
    return apiClient.post("/applications/returns/allocate", data);
  },

  /**
   * Отклонение возврата
   * @param {Number|String} returnId - ID возврата
   * @param {Object} data - { reason }
   * @returns {Promise<Object>}
   */
  async rejectReturn(returnId, data) {
    return apiClient.post(`/applications/returns/${returnId}/reject`, data);
  },
};
